import { Redo2, Undo2 } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Page, PageHeader } from "@/components/layout/page";
import { useToast } from "@/components/ui/toast";
import type { AddAt } from "@/features/intake-flow/actions";
import { builderState } from "@/features/intake-flow/builder-state";
import { Canvas } from "@/features/intake-flow/canvas";
import { ConditionDialog } from "@/features/intake-flow/condition-dialog";
import { NewQuestionDialog } from "@/features/intake-flow/new-question-dialog";
import { StepDrawer } from "@/features/intake-flow/step-drawer";
import { TreeBar } from "@/features/intake-flow/tree-bar";
import {
  VersionHistory,
  VersionHistoryButton,
} from "@/features/intake-flow/version-history";
import { TreeDialog } from "@/features/intake-flow/tree-dialog";
import { VaryByDialog } from "@/features/intake-flow/vary-by-dialog";
import {
  type FlowEdge,
  type FlowNode,
  type Graph,
  type Problem,
  addAfter,
  addAfterNode,
  addBefore,
  addBranch,
  addBranchHere,
  addCatchAll,
  addFirst,
  askedBefore,
  branchOnAnswer,
  keyFrom,
  nodeOf,
  planDelete,
  outOf,
  removeBranch,
  undoSplit,
  removeNode,
  updateEdge,
  updateNode,
  varyBy,
  varyBySoFar,
} from "@/lib/intake-flow";
import { useCallDispositions, useCampaigns, useFirms } from "@/lib/queries";
import {
  useCreateDraft,
  useCurrentFlow,
  useCreateQuestion,
  useFlows,
  useRestoreVersion,
  useFlowQuestions,
  useLeadFormSections,
  usePublishFlow,
  useSaveGraph,
} from "@/lib/queries-intake";
import {
  type TreeSummary,
  useActivateTree,
  useArchiveTree,
  useCreateTree,
  useDuplicateTree,
  useReorderTrees,
  useTrees,
  useUpdateTree,
} from "@/lib/queries-intake-trees";
import { useLeaveGuard } from "@/lib/leave-guard";
import { useHistory } from "@/lib/use-history";

/**
 * The intake flow builder (ACL-338).
 *
 * An admin draws the tree of questions a rep is asked during a call: what is asked first, what
 * each answer leads to, and what the intake ends in. The canvas and what is stored are the same
 * object, so the page saves the whole graph at once.
 *
 * A published version is read-only: editing it means making a draft, which is a copy. Leads
 * already mid-intake keep walking the version they started on, so publishing never changes the
 * questions under a rep on a call.
 */
export default function IntakeFlowPage() {
  const toast = useToast();
  const trees = useTrees();
  // Which tree the canvas is showing. Null until the list arrives, and then the first one, which
  // is the tree the engine tries first (ACL-340).
  const [treeId, setTreeId] = React.useState<number | null>(null);
  const rows = React.useMemo(() => trees.data?.trees ?? [], [trees.data]);
  React.useEffect(() => {
    if (treeId !== null && rows.some((one) => one.id === treeId)) return;
    setTreeId(rows[0]?.id ?? null);
  }, [rows, treeId]);
  const tree = rows.find((one) => one.id === treeId) ?? null;

  const createTree = useCreateTree();
  const updateTree = useUpdateTree();
  const duplicateTree = useDuplicateTree();
  const reorderTrees = useReorderTrees();
  const archiveTree = useArchiveTree();
  const activateTree = useActivateTree();
  const [namingTree, setNamingTree] = React.useState<{ source: TreeSummary | null } | null>(null);

  const current = useCurrentFlow(treeId ?? undefined);
  const flow = current.data ?? null;
  const questions = useFlowQuestions(flow?.id);
  const createDraft = useCreateDraft();
  const saveGraph = useSaveGraph();
  /**
   * Make a draft, and say so when it cannot be made.
   *
   * The route refuses with a sentence worth reading — "this tenant has no tree yet: make one
   * before drafting a flow" — and this mutation was the one on the page with no `onError`, so
   * the click did nothing at all and said nothing about why (ACL-478).
   */
  const draftHere = () =>
    createDraft.mutate(treeId ?? undefined, {
      onError: (error) => toast.error("That did not make a draft", (error as Error).message),
    });
  const publish = usePublishFlow();
  const sections = useLeadFormSections();
  const createQuestion = useCreateQuestion(flow?.id);
  // The same two routes the Firms and Campaigns pages read, so the builder lists exactly what
  // those screens show rather than a second view of the same rows.
  const firms = useFirms();
  const campaigns = useCampaigns();
  // The whole catalog, which is what the validator checks a reject against.
  const dispositions = useCallDispositions(null);

  const history = useHistory<Graph>({ start: null, state_field_key: null, nodes: [], edges: [] });
  const [baseline, setBaseline] = React.useState<string>("");
  const [editing, setEditing] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState<AddAt | undefined>(undefined);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  // A step added by "What comes next?" is on the canvas before its drawer is filled in, so
  // Cancel has to take it back off again: closing the drawer without saving should leave the
  // flow exactly as it was, not strand a half-made card nobody asked to keep.
  // A ref, not state: the drawer saves and closes in one gesture, and React batches the two, so
  // a state flag would still read "pending" in the close handler and delete the step just kept.
  const pending = React.useRef<string | null>(null);
  const [branching, setBranching] = React.useState<{ key: string; position: number } | null>(null);
  const [newQuestion, setNewQuestion] = React.useState(false);
  const [varying, setVarying] = React.useState<string | null>(null);
  const [publishing, setPublishing] = React.useState(false);
  const [showingHistory, setShowingHistory] = React.useState(false);
  const historyVersions = useFlows(showingHistory && treeId ? treeId : undefined);
  const restoreVersion = useRestoreVersion();

  // The server's graph is the page's starting point, taken ONCE per version: every edit after
  // that is local until Save.
  //
  // Not on `flow.nodes` and `flow.edges`: those are arrays out of the query cache, and a refetch
  // hands back new ones whose contents are identical. React compares them by identity, so the
  // page reloaded the server's copy on every refetch — on a window focus, or when the data went
  // stale mid-edit — and every unsaved step and path vanished the moment it was made.
  const loadedVersion = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (!flow) return;
    const stamp = `${flow.id}:${flow.status}:${flow.published_at ?? ""}`;
    if (loadedVersion.current === stamp) return;
    loadedVersion.current = stamp;
    const loaded: Graph = {
      start: flow.start_key,
      state_field_key: flow.state_field_key,
      nodes: flow.nodes,
      edges: flow.edges,
    };
    history.reset(loaded);
    setBaseline(JSON.stringify(loaded));
    // `history` is stable; re-running on its identity would wipe an edit mid-typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow?.id, flow?.status, flow?.published_at]);

  const graph = history.value;
  const readOnly = !flow || flow.status !== "draft";
  // Which of the builder's three not-yet-drawing states this is (ACL-478).
  const stage = builderState(rows.length, flow !== null, flow?.status === "draft");
  const dirty = baseline !== "" && JSON.stringify(graph) !== baseline;
  const problems: Problem[] = flow?.report?.errors ?? [];
  const warnings: Problem[] = flow?.report?.warnings ?? [];

  // Leaving with unsaved changes asks first, however the page is left (ACL-404). Before, only a
  // reload or a closed tab asked: a link in the console and the browser's Back left without a word.
  const { dialog: leaveDialog } = useLeaveGuard(dirty, "The changes to this intake flow are not saved. Leaving drops them.");

  if (current.isLoading) return <Spinner />;
  if (current.error) return <ErrorState error={current.error} title="The intake flow" />;

  // Only the questions this step's own path has already asked are out of bounds: asking one
  // twice on one path would overwrite its answer. The same question on two different branches is
  // ordinary, and often the point.
  const asked = new Set(editing ? askedBefore(graph, editing) : []);
  const taken = graph.nodes.map((n) => n.key);

  /** Put a new step where it was asked for: between two steps, after one, before one, or first. */
  function insert(graph: Graph, at: AddAt, node: FlowNode): Graph {
    if (at && "edge" in at) return addAfter(graph, at.edge, node);
    if (at && "after" in at) return addAfterNode(graph, at.after, node);
    if (at && "before" in at) return addBefore(graph, at.before, node);
    return addFirst(graph, node);
  }

  function addStep(at: AddAt, kind: FlowNode["kind"], outcome: FlowNode["outcome"]) {
    const title =
      kind === "question" ? "A new question" : outcome === "flag" ? "Flag for review" : outcome === "reject" ? "Not qualified" : "Qualified";
    const node: FlowNode = {
      key: keyFrom(title, taken),
      kind,
      title,
      field_key: null,
      outcome,
      config: kind === "question" ? { must_answer: true } : {},
    };
    history.set((g) => insert(g, at, node));
    setAdding(undefined);
    pending.current = node.key;
    setEditing(node.key);
  }

  async function save() {
    if (!flow) return;
    try {
      await saveGraph.mutateAsync({ flowId: flow.id, graph });
      setBaseline(JSON.stringify(graph));
      toast.success("Saved");
    } catch (error) {
      toast.error("That did not save", (error as Error).message);
    }
  }

  const deletePlan = deleting ? planDelete(graph, deleting) : null;

  return (
    // The shared page wrapper, as every other screen uses: it is what puts the gutter down each
    // side and keeps the content off the window's edges.
    <Page className="space-y-4">
      <PageHeader
        title="Intake flow"
        description="The questions a rep is asked during a call, and what each answer leads to."
        action={
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={history.undo} disabled={!history.canUndo} aria-label="Undo">
              <Undo2 className="size-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={history.redo} disabled={!history.canRedo} aria-label="Redo">
              <Redo2 className="size-4" />
            </Button>
            {/* Outside the read-only fork: the history is worth reading in either state, and a
                tree whose versions are all retired is exactly the one needing a rollback. */}
            <VersionHistoryButton onClick={() => setShowingHistory(true)} />
            {/* With no tree there is nothing to draft in and nothing to edit: neither fork's
                buttons can do anything, and the empty state below asks for the tree instead
                (ACL-478). */}
            {stage === "no-tree" ? null : readOnly ? (
              <Button onClick={draftHere} loading={createDraft.isPending}>
                Edit as a draft
              </Button>
            ) : (
              <>
                {/* Where an admin looks for it: on the page, not inside a step's drawer. */}
                <Button variant="outline" onClick={() => setNewQuestion(true)}>
                  New question
                </Button>
                <Button variant="outline" onClick={save} loading={saveGraph.isPending} disabled={!dirty}>
                  Save draft
                </Button>
                <Button onClick={() => setPublishing(true)} disabled={dirty || problems.length > 0}>
                  Publish
                </Button>
              </>
            )}
          </div>
        }
      />

      <TreeBar
        trees={rows}
        warnings={trees.data?.warnings ?? []}
        status={
          flow ? (
            // A status, not a warning: a pill the width of its own words, sitting with the tree
            // it is a version of rather than between two callouts.
            <div className="flex items-center gap-2">
              <Badge tone={flow.status === "published" ? "success" : "accent"}>
                Version {flow.version} · {flow.status === "published" ? "live" : flow.status}
              </Badge>
              {flow.status === "published" ? (
                <span className="text-xs text-text-muted">Make a draft to change it.</span>
              ) : null}
            </div>
          ) : null
        }
        selected={treeId}
        readOnly={false}
        onSelect={setTreeId}
        onAdd={() => setNamingTree({ source: null })}
        onDuplicate={(one) => setNamingTree({ source: one })}
        onActivate={(one, active) =>
          activateTree.mutate(
            { treeId: one.id, active },
            {
              onSuccess: () =>
                toast.success(active ? `${one.title} is running` : `${one.title} is stopped`),
              onError: (error) => toast.error("That did not work", (error as Error).message),
            },
          )
        }
        onArchive={(one) =>
          archiveTree.mutate(
            { treeId: one.id, archived: true },
            {
              onSuccess: () => toast.success(`${one.title} is archived`),
              onError: (error) => toast.error("That did not work", (error as Error).message),
            },
          )
        }
        onMove={(one, direction) => {
          const order = rows.map((row) => row.id);
          const at = order.indexOf(one.id);
          const to = at + direction;
          if (at < 0 || to < 0 || to >= order.length) return;
          [order[at], order[to]] = [order[to]!, order[at]!];
          reorderTrees.mutate(order, {
            onError: (error) => toast.error("That did not work", (error as Error).message),
          });
        }}
      />

      {stage === "no-tree" ? (
        /* No tree at all, which is a different thing from a tree with no draft: drafting needs a
           tree to draft in, so offering "Make a draft" here only ever produced a 409 (ACL-478).
           A tenant provisioned since that ticket arrives with a tree already, so this is the
           older tenant, or one whose only tree was archived. */
        <EmptyState
          title="No intake yet"
          description="An intake is a tree of questions a rep is asked on the call. Make the tree first, then draw the questions in it."
          action={
            <Button onClick={() => setNamingTree({ source: null })} loading={createTree.isPending}>
              Make a tree
            </Button>
          }
        />
      ) : !flow ? (
        <EmptyState
          title="No intake flow yet"
          description="Make a draft to start asking questions during a call."
          action={<Button onClick={draftHere} loading={createDraft.isPending}>Make a draft</Button>}
        />
      ) : (
        <>
          {problems.length > 0 ? (
            <Callout tone="danger">
              <p className="font-medium">This flow cannot be published yet.</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-4">
                {problems.map((p, i) => (
                  <li key={`${p.code}-${i}`}>{p.message}</li>
                ))}
              </ul>
            </Callout>
          ) : null}
          {warnings.length > 0 ? (
            <Callout tone="warning">
              <ul className="list-disc space-y-0.5 pl-4">
                {warnings.map((p, i) => (
                  <li key={`${p.code}-${i}`}>{p.message}</li>
                ))}
              </ul>
            </Callout>
          ) : null}

          <Canvas
            graph={graph}
            questions={questions.data ?? []}
            problems={problems}
            readOnly={readOnly}
            firms={firms.data ?? []}
            campaigns={campaigns.data ?? []}
            actions={{
              onAdd: (edge) => setAdding(edge),
              onEdit: (key) => setEditing(key),
              onDelete: (key) => setDeleting(key),
              onAddBranch: (key) => {
                // A new branch with no conditions would be invisible and would shadow the
                // catch-all, so its conditions are asked for straight away.
                //
                // `addBranchHere` makes an end for the branch to land on when the step has
                // nowhere yet. This used to return early instead, so the button did nothing on a
                // step that had just been put down.
                const position = outOf(graph, key).length;
                history.set((g) => addBranchHere(g, key));
                setBranching({ key, position });
              },
              onBranchOnAnswer: (key) => {
                const node = nodeOf(graph, key);
                const question = (questions.data ?? []).find((q) => q.key === node?.field_key);
                if (question) history.set((g) => branchOnAnswer(g, key, question));
              },
              onVaryBy: (key) => setVarying(key),
              onAddCatchAll: (key) => history.set((g) => addCatchAll(g, key)),
              onBranchConditions: (key, position) => setBranching({ key, position }),
              onRemoveBranch: (key, position) => history.set((g) => removeBranch(g, key, position)),
            }}
          />
        </>
      )}

      {adding !== undefined ? (
        <ConfirmDialog
          open
          onOpenChange={() => setAdding(undefined)}
          title="What comes next?"
          description={
            <div className="space-y-2">
              <Button className="w-full" variant="outline" onClick={() => addStep(adding, "question", null)}>
                A question
              </Button>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => {
                  // A Paths step fans out on who the lead is rather than on an answer.
                  const node: FlowNode = {
                    key: keyFrom("Vary by", taken),
                    kind: "paths",
                    title: "Vary by firm or campaign",
                    field_key: null,
                    outcome: null,
                    config: {},
                  };
                  history.set((g) => insert(g, adding ?? null, node));
                  setAdding(undefined);
                  pending.current = node.key;
                  setVarying(node.key);
                }}
              >
                Vary by firm or campaign
              </Button>
              <Button className="w-full" variant="outline" onClick={() => addStep(adding, "outcome", "flag")}>
                Flag for review
              </Button>
              <Button className="w-full" variant="outline" onClick={() => addStep(adding, "outcome", "reject")}>
                Not qualified
              </Button>
              <Button className="w-full" variant="outline" onClick={() => addStep(adding, "outcome", "continue")}>
                Qualified, the intake ends
              </Button>
            </div>
          }
          confirmLabel="Close"
          onConfirm={() => setAdding(undefined)}
        />
      ) : null}

      <StepDrawer
        open={editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          // Cancelled, or dismissed with Escape or a click outside: a step that was never
          // filled in goes back off the canvas, chain and all, as though it was never added.
          const key = pending.current;
          if (key) {
            history.set((g) => removeNode(g, key));
            pending.current = null;
          }
          setEditing(null);
        }}
        node={editing ? (nodeOf(graph, editing) ?? null) : null}
        questions={questions.data ?? []}
        asked={asked}
        canBranchHere={editing ? outOf(graph, editing).length <= 1 : false}
        dispositions={dispositions.data ?? []}
        onSave={(patch) => {
          if (editing) history.set((g) => updateNode(g, editing, patch));
          // Saved, so it is a step of the flow now rather than one still being decided.
          pending.current = null;
        }}
        onNewQuestion={() => setNewQuestion(true)}
        onBranchOnAnswer={(question) => {
          if (!editing) return;
          const key = editing;
          history.set((g) => branchOnAnswer(updateNode(g, key, { field_key: question.key }), key, question));
          pending.current = null;
          setEditing(null);
        }}
      />

      {deleting && deletePlan ? (
        <ConfirmDialog
          open
          onOpenChange={() => setDeleting(null)}
          destructive
          title={`Delete ${nodeOf(graph, deleting)?.title ?? "this step"}?`}
          confirmDisabled={!deletePlan.allowed}
          description={
            deletePlan.allowed ? (
              <div className="space-y-2">
                <p>
                  {deletePlan.going.length === 1
                    ? "The flow carries on from where this step led."
                    : `${deletePlan.going.length} steps go, and the flow carries on from where the branches rejoined.`}
                </p>
                {deletePlan.readsIt > 0 ? (
                  <p>
                    {deletePlan.readsIt} branch {deletePlan.readsIt === 1 ? "condition reads" : "conditions read"} this
                    answer. They stay as they are, and the flow will say they match nobody until you change them.
                  </p>
                ) : null}
                <p className="text-text-muted">
                  The question itself stays on the contact form, and answers leads have already given stay on their
                  records.
                </p>
              </div>
            ) : (
              deletePlan.reason
            )
          }
          confirmLabel={deletePlan.going.length > 1 ? `Delete ${deletePlan.going.length} steps` : "Delete"}
          onConfirm={() => {
            history.set((g) => removeNode(g, deleting));
            setDeleting(null);
          }}
        />
      ) : null}

      <NewQuestionDialog
        open={newQuestion}
        onOpenChange={setNewQuestion}
        sections={sections.data ?? []}
        saving={createQuestion.isPending}
        onCreate={async (body) => {
          try {
            const made = await createQuestion.mutateAsync(body);
            setNewQuestion(false);
            if (editing) {
              // Opened from a step: that step takes the question straight away, so the admin is
              // not sent back to find it in the list.
              history.set((g) => updateNode(g, editing, { field_key: made.key, title: made.title }));
              toast.success("Question added", "This step now asks it.");
            } else {
              // Opened from the page: it is on the contact form and in the picker, waiting to be
              // put on a step.
              toast.success("Question added", "Pick it on any step to ask it.");
            }
          } catch (error) {
            toast.error("That question was not added", (error as Error).message);
          }
        }}
      />

      {/* The Fallback is the LAST path out of a step, not any path that happens to be empty: a
          path just added has no conditions yet and is exactly the one being given some. */}
      <VaryByDialog
        open={varying !== null}
        onOpenChange={(open) => {
          if (open) return;
          // Same as the step drawer: a Vary by step is put down before it is told what to vary
          // on, so cancelling takes it back off rather than leaving an empty split behind.
          const key = pending.current;
          if (key) {
            history.set((g) => removeNode(g, key));
            pending.current = null;
          }
          setVarying(null);
        }}
        firms={firms.data ?? []}
        campaigns={campaigns.data ?? []}
        current={varying ? varyBySoFar(graph, varying) : null}
        onPick={(source, picked) => {
          const key = varying;
          if (!key) return;
          history.set((g) => varyBy(g, key, source, picked));
          pending.current = null;
          setVarying(null);
        }}
      />

      {branching ? (
        <ConditionDialog
          open
          onOpenChange={(open) => setBranching(open ? branching : null)}
          label={outOf(graph, branching.key).find((e) => e.position === branching.position)?.label ?? null}
          conditions={outOf(graph, branching.key).find((e) => e.position === branching.position)?.conditions ?? []}
          fallback={outOf(graph, branching.key).at(-1)?.position === branching.position}
          questions={questions.data ?? []}
          askedBefore={askedBefore(graph, branching.key)}
          firms={firms.data ?? []}
          campaigns={campaigns.data ?? []}
          onSave={(next) => {
            history.set((g) => updateEdge(g, branching.key, branching.position, next));
            setBranching(null);
          }}
          onUndoSplit={() => {
            history.set((g) => undoSplit(g, branching.key));
            setBranching(null);
          }}
        />
      ) : null}

      {publishing && flow ? (
        <ConfirmDialog
          open
          onOpenChange={() => setPublishing(false)}
          title={`Publish version ${flow.version}?`}
          description="New leads are asked this from now on. Anyone already part-way through the intake keeps the version they started on, so nothing changes under a rep on a call."
          confirmLabel="Publish"
          loading={publish.isPending}
          onConfirm={async () => {
            try {
              await publish.mutateAsync({ flowId: flow.id });
              toast.success("Published");
            } catch (error) {
              toast.error("That did not publish", (error as Error).message);
            }
            setPublishing(false);
          }}
        />
      ) : null}
      <VersionHistory
        open={showingHistory}
        onOpenChange={setShowingHistory}
        versions={historyVersions.data ?? []}
        loading={historyVersions.isLoading}
        treeTitle={tree?.title ?? "This tree"}
        treeActive={Boolean(tree?.active)}
        hasDraft={Boolean(tree?.draft_id)}
        restoring={restoreVersion.isPending}
        onRestore={(one) =>
          restoreVersion.mutate(one.id, {
            onSuccess: (made) => {
              setShowingHistory(false);
              toast.success(`v${one.version} is back as draft v${made.version}`, "Publish it when it is right.");
            },
            onError: (error) => toast.error("That did not work", (error as Error).message),
          })
        }
      />

      <TreeDialog
        open={namingTree !== null}
        onOpenChange={(open) => !open && setNamingTree(null)}
        source={namingTree?.source ?? null}
        taken={rows.map((one) => one.slug)}
        onSave={({ slug, title }) => {
          const source = namingTree?.source ?? null;
          const after = (made: TreeSummary) => {
            setTreeId(made.id);
            toast.success(`${made.title} is ready`);
          };
          const failed = (error: unknown) =>
            toast.error("That did not work", (error as Error).message);
          if (source) {
            duplicateTree.mutate(
              { treeId: source.id, slug, title },
              { onSuccess: after, onError: failed },
            );
          } else {
            createTree.mutate({ slug, title }, { onSuccess: after, onError: failed });
          }
        }}
      />

      {leaveDialog}
    </Page>
  );
}
